/**
 * scriptParser — A/V script parser/serializer for the Video Generator's
 * `in_motion_prompt` field.
 *
 * Layer on top of the existing `parseMotionPrompt` (in VideoGeneratorNode.tsx).
 * Adds:
 *   - Per-block client IDs (so the editor can address each block stably).
 *   - Track separation: `videoBlocks` (action) vs `audioBlocks`
 *     (dialogue / voiceover / sfx).
 *   - Disable reason detection for Structured mode (BGM lines,
 *     leading non-segment text such as `Graphic style: ...`).
 *   - Video & audio overlap detection.
 *   - Byte-stable serialization back to `in_motion_prompt`.
 */

import {
  parseMotionPrompt,
  type MotionSegment,
} from './VideoGeneratorNode'

export type SegmentKind = 'video' | 'dialogue' | 'voiceover' | 'sfx' | 'bgm'

export interface ScriptBlock {
  id: string
  kind: SegmentKind
  startSec: number
  endSec: number
  text: string
  character?: string
  emotion?: string
  camera?: string
}

export type StructuredDisable =
  | { reason: 'leading-non-segment'; sample: string }
  | { reason: 'bgm-present'; sample: string }
  | { reason: 'unparseable' }

export interface ParsedScript {
  clipHeader: string | null
  videoBlocks: ScriptBlock[]
  audioBlocks: ScriptBlock[]
  videoOverlap: Array<[string, string]>
  audioOverlapCount: number
  canStructured: boolean
  disableReason: StructuredDisable | null
}

/* ── ID generation ── */

let _counter = 0
function generateId(): string {
  // Use crypto.randomUUID when available; fall back to counter for SSR / tests.
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // ignore
  }
  _counter += 1
  return `blk-${_counter}`
}

/* ── Helpers ── */

const SFX_PREFIX_RE = /^\s*(SFX|Sound)\s*:/i
const BGM_LINE_RE = /^\s*BGM\s*[:-]/im

function trimSample(text: string, limit = 60): string {
  const t = text.trim().replace(/\s+/g, ' ')
  return t.length > limit ? `${t.slice(0, limit)}…` : t
}

function segmentToBlock(seg: MotionSegment): ScriptBlock {
  if (seg.type === 'action') {
    return {
      id: generateId(),
      kind: 'video',
      startSec: seg.startTime,
      endSec: seg.endTime,
      text: seg.action,
      camera: seg.camera ?? undefined,
    }
  }
  // dialogue or voiceover — check for SFX shorthand.
  // Existing parser may have promoted "SFX:" into the `character` slot via
  // the colon-fallback path; treat both cases as SFX.
  const characterLooksLikeSfx = /^(SFX|Sound)$/i.test((seg.character ?? '').trim())
  const bodyStartsWithSfx = SFX_PREFIX_RE.test(seg.action)
  const isSfx = characterLooksLikeSfx || bodyStartsWithSfx
  let text = seg.action
  if (bodyStartsWithSfx) {
    text = seg.action.replace(SFX_PREFIX_RE, '').trim()
  }
  return {
    id: generateId(),
    kind: isSfx ? 'sfx' : seg.type === 'voiceover' ? 'voiceover' : 'dialogue',
    startSec: seg.startTime,
    endSec: seg.endTime,
    text,
    character: isSfx ? undefined : seg.character,
    emotion: isSfx ? undefined : seg.emotion,
  }
}

function overlaps(a: ScriptBlock, b: ScriptBlock): boolean {
  return a.startSec < b.endSec && b.startSec < a.endSec
}

function detectOverlapPairs(blocks: ScriptBlock[]): Array<[string, string]> {
  const pairs: Array<[string, string]> = []
  for (let i = 0; i < blocks.length; i++) {
    for (let j = i + 1; j < blocks.length; j++) {
      if (overlaps(blocks[i], blocks[j])) {
        pairs.push([blocks[i].id, blocks[j].id])
      }
    }
  }
  return pairs
}

function maxConcurrent(blocks: ScriptBlock[]): number {
  if (blocks.length === 0) return 0
  // Sweep over time-points.
  const events: Array<{ t: number; delta: number }> = []
  for (const b of blocks) {
    events.push({ t: b.startSec, delta: 1 })
    events.push({ t: b.endSec, delta: -1 })
  }
  // End-events must be processed before start-events at the same t,
  // so two touching blocks aren't counted as overlapping.
  events.sort((a, b) => (a.t - b.t) || (a.delta - b.delta))
  let cur = 0
  let max = 0
  for (const ev of events) {
    cur += ev.delta
    if (cur > max) max = cur
  }
  return max
}

function formatSec(n: number): string {
  // Mirror existing serialization style: `1.0s-3.5s` (one decimal).
  return `${(Math.round(n * 10) / 10).toFixed(1)}s`
}

/* ── parseScript ── */

export function parseScript(rawText: string): ParsedScript {
  const text = rawText ?? ''
  const trimmed = text.trim()

  // Empty motion prompt → no structured view, but no error either.
  if (!trimmed) {
    return {
      clipHeader: null,
      videoBlocks: [],
      audioBlocks: [],
      videoOverlap: [],
      audioOverlapCount: 0,
      canStructured: false,
      disableReason: { reason: 'unparseable' },
    }
  }

  // BGM disables structured mode wholesale.
  const bgmMatch = text.match(BGM_LINE_RE)
  if (bgmMatch) {
    return {
      clipHeader: null,
      videoBlocks: [],
      audioBlocks: [],
      videoOverlap: [],
      audioOverlapCount: 0,
      canStructured: false,
      disableReason: { reason: 'bgm-present', sample: trimSample(bgmMatch[0]) },
    }
  }

  const parsed = parseMotionPrompt(text)

  if (parsed.fallbackText || parsed.segments.length === 0) {
    return {
      clipHeader: parsed.clipHeader,
      videoBlocks: [],
      audioBlocks: [],
      videoOverlap: [],
      audioOverlapCount: 0,
      canStructured: false,
      disableReason: { reason: 'unparseable' },
    }
  }

  // Leading-non-segment detection — look at the action block (everything
  // before the first <dialogue>/<voiceover> tag) and check whether the text
  // before the first timestamp is more than just a "ClipHeader:" label.
  const tagIdx = text.search(/<(dialogue|voiceover)>/i)
  const actionBlock = tagIdx >= 0 ? text.slice(0, tagIdx) : text
  const firstTsIdx = actionBlock.search(/\[\d+(?:\.\d+)?s?-\d+(?:\.\d+)?s\]/)
  if (firstTsIdx > 0) {
    const lead = actionBlock.slice(0, firstTsIdx).trim()
    // The existing parser already strips a single-line clip header ending
    // with `:`. Anything else before the first timestamp is "leading non-segment".
    // Acceptable: `Clip 1:` or `Some Header:` (single line, ends with colon).
    const looksLikeClipHeader = /^[^\n]+:\s*$/.test(`${lead}\n`)
    if (lead && !looksLikeClipHeader) {
      return {
        clipHeader: null,
        videoBlocks: [],
        audioBlocks: [],
        videoOverlap: [],
        audioOverlapCount: 0,
        canStructured: false,
        disableReason: { reason: 'leading-non-segment', sample: trimSample(lead) },
      }
    }
  }

  // Build blocks.
  const videoBlocks: ScriptBlock[] = []
  const audioBlocks: ScriptBlock[] = []
  for (const seg of parsed.segments) {
    const blk = segmentToBlock(seg)
    if (blk.kind === 'video') videoBlocks.push(blk)
    else audioBlocks.push(blk)
  }
  videoBlocks.sort((a, b) => a.startSec - b.startSec)
  audioBlocks.sort((a, b) => a.startSec - b.startSec)

  const videoOverlap = detectOverlapPairs(videoBlocks)
  const audioMax = maxConcurrent(audioBlocks)
  const audioOverlapCount = audioMax >= 2 ? 2 : audioMax >= 1 ? 1 : 0

  return {
    clipHeader: parsed.clipHeader,
    videoBlocks,
    audioBlocks,
    videoOverlap,
    audioOverlapCount,
    canStructured: videoBlocks.length + audioBlocks.length > 0,
    disableReason: null,
  }
}

/* ── serializeScript ── */

function escapeBody(s: string): string {
  return s.replace(/\s+$/g, '')
}

function serializeVideoBlock(b: ScriptBlock): string {
  const ts = `[${formatSec(b.startSec)}-${formatSec(b.endSec)}]`
  let out = `${ts}: ${escapeBody(b.text)}`
  if (b.camera && b.camera.trim()) {
    out += `\n  Camera: ${b.camera.trim()}`
  }
  return out
}

function serializeSpeechBlock(b: ScriptBlock): string {
  const ts = `[${formatSec(b.startSec)}-${formatSec(b.endSec)}]`
  if (b.kind === 'sfx') {
    return `${ts}: SFX: ${escapeBody(b.text)}`
  }
  // dialogue / voiceover
  const character = (b.character ?? '').trim()
  const emotion = (b.emotion ?? '').trim()
  const speakerInner = emotion ? `${character},${emotion}` : character
  const body = escapeBody(b.text)
  // Skip `""` wrapper when body is empty so reopening the block doesn't
  // surface literal quote characters in the editor. `[@Alice]:` (with or
  // without speaker) round-trips cleanly through `parseTimestampedSegments`.
  if (!body) {
    return speakerInner ? `${ts}: [@${speakerInner}]:` : `${ts}:`
  }
  const speaker = speakerInner ? `[@${speakerInner}]: ` : ''
  return `${ts}: ${speaker}"${body}"`
}

export function serializeScript(parsed: {
  clipHeader: string | null
  videoBlocks: ScriptBlock[]
  audioBlocks: ScriptBlock[]
}): string {
  const parts: string[] = []

  // Action block.
  const video = [...parsed.videoBlocks].sort((a, b) => a.startSec - b.startSec)
  const speech = [...parsed.audioBlocks].sort((a, b) => a.startSec - b.startSec)
  const dialogueBlocks = speech.filter((b) => b.kind === 'dialogue' || b.kind === 'sfx')
  const voiceoverBlocks = speech.filter((b) => b.kind === 'voiceover')

  if (parsed.clipHeader) {
    parts.push(`${parsed.clipHeader}:`)
  }
  for (const b of video) {
    parts.push(serializeVideoBlock(b))
  }

  let out = parts.join('\n')

  if (dialogueBlocks.length > 0) {
    if (out.length > 0) out += '\n'
    out += '<dialogue>\n'
    out += dialogueBlocks.map(serializeSpeechBlock).join('\n')
  }
  if (voiceoverBlocks.length > 0) {
    if (out.length > 0) out += '\n'
    out += '<voiceover>\n'
    out += voiceoverBlocks.map(serializeSpeechBlock).join('\n')
  }

  return out
}
